import { BaseModel } from './base-model';
import { Address } from './address';
import { Campaign, CampaignProduct, CRMField, FormatRule, Image, SelectOption } from '.';

/* Checkout Builder */
export interface BuilderConfig {
  build_mode: boolean;
  campaigns: Campaign[];
}

export enum DigitalPayment {
  Google,
  Apple,
  Paypal,
}

enum CardType {
  None,
  Visa,
  Mastercard,
  AmericanExpress,
  Discover,
  Other,
}

export enum GatewayType {
  CyberSource = 1,
}

export enum GatewayDashboardSections {
  TransactionSummary,
  ApprovalAnalytics,
}

export enum ProcessorType {
  FDIGlobal = 1,
  ChasePaymentTech = 2,
  Elavon = 3,
  Tsys = 4,
  SmartFDC = 5,
  Streamline2 = 6,
  VPCQorpay = 7,
}

export enum MicroformSection {
  IncludeCDN,
  PrepareForm,
  InitializeMicroform,
  CodeChanges,
  ConfigurationReference,
  CompleteExample,
  Troubleshooting,
}

export enum MicroformCodeBlock {
  Cdn,
  RequiredFields,
  ExampleForm,
  Init,
  CodeChanges,
  CompleteExample,
}

export enum MicroformSubmitMode {
  Form,
  CustomAsync,
  CustomSyncNoRedirect,
  CustomSync,
  AutoIntercept,
}

export const MicroformSubmitModeTokens = {};
MicroformSubmitModeTokens[MicroformSubmitMode.Form] = 'FORM';
MicroformSubmitModeTokens[MicroformSubmitMode.CustomAsync] = 'CUSTOM_ASYNC';
MicroformSubmitModeTokens[MicroformSubmitMode.CustomSyncNoRedirect] = 'CUSTOM_SYNC_NO_REDIRECT';
MicroformSubmitModeTokens[MicroformSubmitMode.CustomSync] = 'CUSTOM_SYNC';
MicroformSubmitModeTokens[MicroformSubmitMode.AutoIntercept] = 'AUTO_INTERCEPT';

export const MicroformSubmitModeLabels = {};
MicroformSubmitModeLabels[MicroformSubmitMode.Form] = 'Standard Form Checkout';
MicroformSubmitModeLabels[MicroformSubmitMode.CustomAsync] = 'Custom Submit with Async/Await';
MicroformSubmitModeLabels[MicroformSubmitMode.CustomSyncNoRedirect] = 'Custom Submit with No Redirect';
MicroformSubmitModeLabels[MicroformSubmitMode.CustomSync] = 'Custom Submit without Async/Await';
MicroformSubmitModeLabels[MicroformSubmitMode.AutoIntercept] = 'Auto Intercept';

export const MicroformSubmitModeHints = {};
MicroformSubmitModeHints[MicroformSubmitMode.Form] =
  'Just include our script, point it at your <form> fields, and let the form submit normally while we handle DDC timing.';
MicroformSubmitModeHints[MicroformSubmitMode.CustomAsync] =
  'Keep your own click handler but mark it async and await solvpathSubmitPayment() before reading card data so DDC ' +
  'finishes before you redirect or call your backend. Note: if 3D Secure step-up is required, the awaited promise ' +
  'resolves once verification is submitted, not once the customer finishes the challenge.';
MicroformSubmitModeHints[MicroformSubmitMode.CustomSyncNoRedirect] =
  'Call solvpathSubmitPayment() after your validation, stay on the same page (no reloads), and render your ' +
  'confirmation. Use this when DDC is not required.';
MicroformSubmitModeHints[MicroformSubmitMode.CustomSync] =
  'Stay synchronous by passing a callback into solvpathSubmitPayment(callback); the callback fires once card data ' +
  'is ready and you can submit or redirect. Note: if 3D Secure step-up is required, the callback fires once ' +
  'verification is submitted, not once the customer finishes the challenge.';
MicroformSubmitModeHints[MicroformSubmitMode.AutoIntercept] =
  'Use this when your submit button is wired up in your own app code and you do not want to change how you ' +
  'submit. We detect the click, tokenize the card first, then let your normal checkout flow continue.';

export const MicroformSubmitModeSnippets = {};
MicroformSubmitModeSnippets[MicroformSubmitMode.CustomAsync] =
  `document.getElementById('submit-btn').addEventListener('click', async function handleSubmit() {
  await solvpathSubmitPayment(); // add this at the start

  // rest of your code (read card data, call APIs, redirect, etc.)
});`;
MicroformSubmitModeSnippets[MicroformSubmitMode.CustomSyncNoRedirect] =
  `document.getElementById('submit-btn').addEventListener('click', function handleSubmit() {
  solvpathSubmitPayment(); // add this near the top, no callback needed

  // keep the user on the same page:
  // - update banners, show success state, unlock UI, etc.
  // - do NOT redirect or reload after this line
});`;
MicroformSubmitModeSnippets[MicroformSubmitMode.CustomSync] =
  `document.getElementById('submit-btn').addEventListener('click', function handleSubmit() {
  solvpathSubmitPayment(function onReady() {
    // move the code that needs card data into this callback
    // e.g., read inputs, send to your server, redirect, etc.
  });

  // any UI updates that should happen immediately can stay after the call
});`;

export const ProcessorTypeLabels = {};
ProcessorTypeLabels[ProcessorType.FDIGlobal] = 'FDI Global';
ProcessorTypeLabels[ProcessorType.ChasePaymentTech] = 'Chase PaymentTech';
ProcessorTypeLabels[ProcessorType.Elavon] = 'Elavon';
ProcessorTypeLabels[ProcessorType.Tsys] = 'TSYS';
ProcessorTypeLabels[ProcessorType.SmartFDC] = 'Smart FDC';
ProcessorTypeLabels[ProcessorType.Streamline2] = 'Streamline 2';
ProcessorTypeLabels[ProcessorType.VPCQorpay] = 'VPC Qorpay';

export enum GatewayTransactionStatus {
  Authorized = 'AUTHORIZED',
  PartialAuthorized = 'PARTIAL_AUTHORIZED',
  AuthorizedPendingReview = 'AUTHORIZED_PENDING_REVIEW',
  Pending = 'PENDING',
  Voided = 'VOIDED',
  AuthorizedRiskDeclined = 'AUTHORIZED_RISK_DECLINED',
  PendingAuthentication = 'PENDING_AUTHENTICATION',
  PendingReview = 'PENDING_REVIEW',
  Declined = 'DECLINED',
  InvalidRequest = 'INVALID_REQUEST',
}

export const GatewayTransactionStatusLabels = {};
GatewayTransactionStatusLabels[GatewayTransactionStatus.Authorized] = 'Authorized';
GatewayTransactionStatusLabels[GatewayTransactionStatus.PartialAuthorized] = 'Partial Authorized';
GatewayTransactionStatusLabels[GatewayTransactionStatus.AuthorizedPendingReview] = 'Authorized Pending Review';
GatewayTransactionStatusLabels[GatewayTransactionStatus.Pending] = 'Pending';
GatewayTransactionStatusLabels[GatewayTransactionStatus.Voided] = 'Voided';
GatewayTransactionStatusLabels[GatewayTransactionStatus.AuthorizedRiskDeclined] = 'Authorized Risk Declined';
GatewayTransactionStatusLabels[GatewayTransactionStatus.PendingAuthentication] = 'Pending Authentication';
GatewayTransactionStatusLabels[GatewayTransactionStatus.PendingReview] = 'Pending Review';
GatewayTransactionStatusLabels[GatewayTransactionStatus.Declined] = 'Declined';
GatewayTransactionStatusLabels[GatewayTransactionStatus.InvalidRequest] = 'Invalid Request';

export enum GatewayTransactionType {
  Sale = 'SALE',
  Authorize = 'AUTHORIZE',
  Capture = 'CAPTURE',
  Void = 'VOID',
  Refund = 'REFUND',
}

export const GatewayTransactionTypeLabels = {};
GatewayTransactionTypeLabels[GatewayTransactionType.Sale] = 'Sale';
GatewayTransactionTypeLabels[GatewayTransactionType.Authorize] = 'Authorize';
GatewayTransactionTypeLabels[GatewayTransactionType.Capture] = 'Capture';
GatewayTransactionTypeLabels[GatewayTransactionType.Void] = 'Void';
GatewayTransactionTypeLabels[GatewayTransactionType.Refund] = 'Refund';

export enum GatewayCardType {
  None = 'NONE',
  Visa = 'VISA',
  Mastercard = 'MASTERCARD',
  Amex = 'AMEX',
  Discover = 'DISCOVER',
  Other = 'OTHER',
}

export const GatewayCardTypeLabels = {};
GatewayCardTypeLabels[GatewayCardType.None] = 'None';
GatewayCardTypeLabels[GatewayCardType.Visa] = 'Visa';
GatewayCardTypeLabels[GatewayCardType.Mastercard] = 'Mastercard';
GatewayCardTypeLabels[GatewayCardType.Amex] = 'Amex';
GatewayCardTypeLabels[GatewayCardType.Discover] = 'Discover';
GatewayCardTypeLabels[GatewayCardType.Other] = 'Other';

interface CheckoutStyles {
  background_color: string;
  primary_text_color: string;
  button_color: string;
  button_text_color: string;
  font_style: string;
  button_style: {
    background_color: string;
    border_color: string;
    border_radius: string;
    color: string;
  };
  options: {
    phone: boolean;
    logo_with_store_name: boolean;
    product_title: boolean;
    secure_payment_text: boolean;
  };
  payment_options: {
    sub_total: boolean;
    tax_support: boolean;
    shipping_fee: boolean;
    payment_details: boolean;
    price_on_button: boolean;
  };
}

interface ButtonControls {
  enable: boolean;
  text: string;
  redirect_url: string;
}

export enum CheckoutReviewStatus {
  None = 0,
  Approved = 1,
  Declined = 2,
  UnderReview = 3,
}

export enum CheckoutStatus {
  Inactive = 0,
  Active = 1,
  Archived = 2,
  Draft = 3,
  Disabled = 4,
}

export enum CheckoutConfirmationControl {
  OrderDetails = 0,
  BillingAddress = 1,
  ShippingAddress = 2,
}

export enum CheckoutField {
  ContactInfo = 0,
  ShippingAddress = 1,
}

export enum CheckoutDesignElement {
  PhoneNumber = 0,
  Logo = 1,
  ProductTitle = 2,
  PriceOnButton = 3,
  TaxSupport = 4,
  ShippingFee = 5,
  PaymentDetails = 6,
  SecurePaymentsText = 7,
}

export interface CheckoutSettings {
  id: number | string;
  account: number | string;
  account_type: number;
  template: CheckoutTemplate;
  campaign: number | string;
  name: string;
  country: string;
  main_product: CampaignProduct;
  review_status: CheckoutReviewStatus;
  digital_payment: DigitalPayment[];
  card_types: CardType[];
  discount_code: boolean;
  store_policies: boolean;
  privacy_policy: boolean;
  currency: string;
  related_products: CampaignProduct[];
  card_upsell: CampaignProduct;
  popup_upsell: CampaignProduct;
  fields: CheckoutField[];
  status: CheckoutStatus;
  styles: CheckoutStyles | {};
  brand_logo: Image;
  sales_banner: Image;
  sales_banner_mobile: Image;
  slim_banner_config: DesignBannerConfig;
  logo_position: DesignLogoPosition;
  elements: CheckoutDesignElement[];
  confirmation_title: string;
  confirmation_message: string;
  confirmation_controls: CheckoutConfirmationControl[];
  cta_controls: ButtonControls | {};
  support_button_controls: ButtonControls | {};
  tracking_pixels: string[];
  google_index: boolean;
  tracking_url_slug: string;
}

interface DesignBannerConfig {
  text: string;
  height: string;
  text_color: string;
  background_color: string;
}

enum DesignLogoPosition {
  Left = 1,
  Center = 2,
  Right = 3,
}

export interface CheckoutConfig {
  config: BuilderConfig;
  checkout: CheckoutSettings;
}

export const CheckoutConfigDefaults: CheckoutConfig = {
  config: {
    build_mode: false,
    campaigns: [],
  },
  checkout: {
    id: null,
    account: null,
    account_type: null,
    template: {} as CheckoutTemplate,
    campaign: null,
    name: '',
    country: 'US',
    review_status: CheckoutReviewStatus.None,
    status: CheckoutStatus.Inactive,
    main_product: null,
    digital_payment: [],
    card_types: [],
    discount_code: true,
    store_policies: true,
    privacy_policy: true,
    currency: 'usd',
    related_products: [],
    card_upsell: null,
    popup_upsell: null,
    fields: [CheckoutField.ContactInfo, CheckoutField.ShippingAddress],
    styles: {
      background_color: '#FFFFFF',
      primary_text_color: '#163549',
      button_color: '#4083e9',
      button_text_color: '#FFFFFF',
      font_style: 'Nunito',
      button_style: {
        background_color: '#4083e9',
        border_color: '#ffffff',
        border_radius: '5px',
        color: '#fff',
      },
      options: {
        phone: true,
        logo_with_store_name: true,
        product_title: true,
        secure_payment_text: true,
      },
      payment_options: {
        sub_total: true,
        tax_support: true,
        shipping_fee: true,
        payment_details: true,
        price_on_button: true,
      },
    },
    brand_logo: null,
    logo_position: DesignLogoPosition.Center,
    sales_banner: null,
    sales_banner_mobile: null,
    slim_banner_config: {
      text: '<p><span style="font-size: 18px; font-family: ubuntu, sans-serif;">100% Encrypted &amp; Secure Checkout</span></p>',
      height: '42',
      text_color: '#ffffff',
      background_color: '#0D3449',
    },
    elements: [CheckoutDesignElement.PhoneNumber],
    confirmation_title: 'Thank you for your purchase!',
    confirmation_message: 'Your order will be processed within the next 24 hours',
    confirmation_controls: [
      CheckoutConfirmationControl.OrderDetails,
      CheckoutConfirmationControl.BillingAddress,
      CheckoutConfirmationControl.ShippingAddress,
    ],
    cta_controls: {
      enable: true,
      text: 'Manage my order',
      redirect_url: '',
    },
    support_button_controls: {
      enable: true,
      text: 'Contact Support',
      redirect_url: '',
    },
    tracking_pixels: [],
    google_index: false,
    tracking_url_slug: '',
  },
};

/* ============================ */

export enum BuilderProductType {
  Main,
  Related,
  Upsell,
  Popup,
}

export interface Checkout extends BaseModel {
  name: string;
  url: string;
  template_type: CheckoutTemplateType;
  account_type: CheckoutAccountType;
  review_status: CheckoutReviewStatus;
  status: CheckoutStatus;
  visitors?: number;
  orders?: number;
  revenue?: string;
  aov?: string;
  cvr?: string;
  updated: string;
  created: string;
  campaign?: number;
  showThreeDotMenu?: boolean;
  deployment_status: DeploymentStatus;
}

export enum DeploymentStatus {
  None = 0,
  Deploying = 1,
  Deployed = 2,
  Error = 3,
}

export const DeploymentStatusLabels = {
  [DeploymentStatus.None]: '--',
  [DeploymentStatus.Deploying]: 'Deploying',
  [DeploymentStatus.Deployed]: 'Deployed',
  [DeploymentStatus.Error]: 'Error',
};

export interface CheckoutTemplate extends BaseModel {
  name: string;
  is_verified: boolean;
  is_visible: boolean;
  type: CheckoutTemplateType;
  thumbnail: string;
  checkout_preview: string;
  confirmation_preview: string;
}

export enum CheckoutGatewayType {
  Unverified = 0,
  Verified = 1,
}

export const CheckoutGatewayTypeLabels = {};
CheckoutGatewayTypeLabels[CheckoutGatewayType.Unverified] = 'Unverified';
CheckoutGatewayTypeLabels[CheckoutGatewayType.Verified] = 'Verified';

export enum CheckoutAccountType {
  Unverified = 0,
  VerifiedWithSpProcessing = 1,
  VerifiedWith3rdPartyProcessing = 2,
}

export const CheckoutAccountTypeLabels = {};
CheckoutAccountTypeLabels[CheckoutAccountType.Unverified] = 'Unverified';
CheckoutAccountTypeLabels[CheckoutAccountType.VerifiedWithSpProcessing] = 'Verified';
CheckoutAccountTypeLabels[CheckoutAccountType.VerifiedWith3rdPartyProcessing] = 'Verified';

export enum CheckoutTemplateType {
  ShoppingCart = 0,
  FreeTrial = 1,
  OneTimePurchase = 2,
  BundlePackage = 3,
  FixedSubscription = 4,
}

export const CheckoutTemplateTypeLabels = {};
CheckoutTemplateTypeLabels[CheckoutTemplateType.ShoppingCart] = 'Shopping Cart';
CheckoutTemplateTypeLabels[CheckoutTemplateType.FreeTrial] = 'Free Trial';
CheckoutTemplateTypeLabels[CheckoutTemplateType.OneTimePurchase] = 'One Time Purchase';
CheckoutTemplateTypeLabels[CheckoutTemplateType.BundlePackage] = 'Bundle Package';
CheckoutTemplateTypeLabels[CheckoutTemplateType.FixedSubscription] = 'Fixed Subscription';

export const CheckoutTemplateTypeDescription = {};
CheckoutTemplateTypeDescription[CheckoutTemplateType.ShoppingCart] = '';
CheckoutTemplateTypeDescription[CheckoutTemplateType.FreeTrial] = '';
CheckoutTemplateTypeDescription[CheckoutTemplateType.OneTimePurchase] = '';
CheckoutTemplateTypeDescription[CheckoutTemplateType.BundlePackage] = '';
CheckoutTemplateTypeDescription[CheckoutTemplateType.FixedSubscription] =
  'As an online business owner looking to increase your revenue, selling subscriptions directly from your website can be ' +
  "a game-changer. With custom checkout forms, you don't need any coding skills to get started. You cantailor the checkout " +
  'experience to your business needs, from pricing to design, making it easy for customers tosubscribe and stay subscribed.';

export const CheckoutReviewStatusTypesLabels = {};
CheckoutReviewStatusTypesLabels[CheckoutReviewStatus.None] = 'None';
CheckoutReviewStatusTypesLabels[CheckoutReviewStatus.Approved] = 'Approved';
CheckoutReviewStatusTypesLabels[CheckoutReviewStatus.Declined] = 'Declined';
CheckoutReviewStatusTypesLabels[CheckoutReviewStatus.UnderReview] = 'Under Review';

export enum CheckoutPopup {
  Create,
  Update,
  Duplicate,
  Archive,
  Delete,
  Unarchive,
}

export const TableStatusLabels = {};
TableStatusLabels[CheckoutStatus.Active] = 'Active';
TableStatusLabels[CheckoutStatus.Inactive] = 'Inactive';
TableStatusLabels[CheckoutStatus.Draft] = 'Draft';
TableStatusLabels[CheckoutStatus.Disabled] = 'Disabled';
TableStatusLabels[CheckoutStatus.Archived] = 'Archived';

export enum CheckoutDateType {
  LastModified = 'lastModified',
  DateCreated = 'dateCreated',
}


export enum PermissionType {
  Bank = -1,
  BusinessType = 0,
  ProductType = 1,
  CheckoutType = 2,
}

export enum ProfileUserRole {
  SuperAdmin = 0,
  Admin = 1,
  Lead = 2,
  Editor = 3,
  Agent = 4,
}

export enum ProfileUser {
  Bank = 0,
  Processor = 1,
}

export enum BankStatus {
  Archived = -1,
  Inactive = 0,
  Active = 1,
  PendingApproval = 2,
}

export const BankStatusLabels = {};
BankStatusLabels[BankStatus.Archived] = 'Archived';
BankStatusLabels[BankStatus.Inactive] = 'InActive';
BankStatusLabels[BankStatus.Active] = 'Active';
BankStatusLabels[BankStatus.PendingApproval] = 'Pending Approval';

export enum BankAccessLevel {
  All = 0,
}

export const BankAccessLevelLabels = {};
BankAccessLevelLabels[BankAccessLevel.All] = 'All Access';

export const BankAccessLevelsList = [BankAccessLevel.All];

export enum BankContactTitle {
  Owner = 1,
}

export const BankContactTitleLabels = {};
BankContactTitleLabels[BankContactTitle.Owner] = 'Owner';

export const BankContactTitleList = [BankContactTitle.Owner];

export enum BusinessType {
  Automotive = 1,
  Software = 2,
  ConsumerGoods = 3,
  Education = 4,
  Entertainment = 5,
  FinancialServices = 6,
  Food = 7,
  Health = 8,
  IT = 9,
  Nonprofits = 10,
  ProfessionalServices = 11,
  RealEstate = 12,
  Sports = 13,
  Transportation = 14,
  Travel = 15,
  Adults = 16,
  Others = 17,
}

export const BusinessTypesList = [
  BusinessType.Automotive,
  BusinessType.Software,
  BusinessType.ConsumerGoods,
  BusinessType.Education,
  BusinessType.Entertainment,
  BusinessType.FinancialServices,
  BusinessType.Food,
  BusinessType.Health,
  BusinessType.IT,
  BusinessType.Nonprofits,
  BusinessType.ProfessionalServices,
  BusinessType.RealEstate,
  BusinessType.Sports,
  BusinessType.Transportation,
  BusinessType.Travel,
  BusinessType.Adults,
  BusinessType.Others,
];

export const BusinessTypeLabels = {};
BusinessTypeLabels[BusinessType.Automotive] = 'Automotive';
BusinessTypeLabels[BusinessType.Software] = 'Computer Software';
BusinessTypeLabels[BusinessType.ConsumerGoods] = 'Consumer Goods & Services';
BusinessTypeLabels[BusinessType.Education] = 'Education';
BusinessTypeLabels[BusinessType.Entertainment] = 'Entertainment';
BusinessTypeLabels[BusinessType.FinancialServices] = 'Financial Services';
BusinessTypeLabels[BusinessType.Food] = 'Food & Beverage';
BusinessTypeLabels[BusinessType.Health] = 'Health & Wellness';
BusinessTypeLabels[BusinessType.IT] = 'Information Technology';
BusinessTypeLabels[BusinessType.Nonprofits] = 'Nonprofits';
BusinessTypeLabels[BusinessType.ProfessionalServices] = 'Professional Services';
BusinessTypeLabels[BusinessType.RealEstate] = 'Real Estate';
BusinessTypeLabels[BusinessType.Sports] = 'Sports';
BusinessTypeLabels[BusinessType.Transportation] = 'Transportation';
BusinessTypeLabels[BusinessType.Travel] = 'Travel & Hospitality';
BusinessTypeLabels[BusinessType.Adults] = 'Adults';
BusinessTypeLabels[BusinessType.Others] = 'Others';

export enum ProductType {
  Medical = 1,
  Beauty = 2,
  CBD = 3,
  Finance = 4,
  Apparel = 5,
  Others = 6,
}

export const ProductTypesList = [
  ProductType.Medical,
  ProductType.Beauty,
  ProductType.CBD,
  ProductType.Finance,
  ProductType.Apparel,
  ProductType.Others,
];

export const ProductTypeLabels = {};
ProductTypeLabels[ProductType.Medical] = 'Medical';
ProductTypeLabels[ProductType.Beauty] = 'Beauty';
ProductTypeLabels[ProductType.CBD] = 'CBD';
ProductTypeLabels[ProductType.Finance] = 'Finance';
ProductTypeLabels[ProductType.Apparel] = 'Apparel';
ProductTypeLabels[ProductType.Others] = 'Others';

export enum CheckoutType {
  StraightSale = 1,
  StraightSaleWithSubscription = 2,
  Subscription = 3,
  Trial = 4,
  Bundle = 5,
  SubscriptionAndSave = 6,
}

export const CheckoutTypesList = [
  CheckoutType.StraightSale,
  CheckoutType.StraightSaleWithSubscription,
  CheckoutType.Subscription,
  CheckoutType.Trial,
  CheckoutType.Bundle,
  CheckoutType.SubscriptionAndSave,
];

export const CheckoutTypeLabels = {};
CheckoutTypeLabels[CheckoutType.StraightSale] = 'Straight Sale';
CheckoutTypeLabels[CheckoutType.StraightSaleWithSubscription] = 'Straight Sale with Subscription';
CheckoutTypeLabels[CheckoutType.Subscription] = 'Subscription';
CheckoutTypeLabels[CheckoutType.Trial] = 'Trial';
CheckoutTypeLabels[CheckoutType.Bundle] = 'Bundle';
CheckoutTypeLabels[CheckoutType.SubscriptionAndSave] = 'Subscription And Save';

export interface Contact extends BaseModel {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  access_level: BankAccessLevel;
  contact_title: BankContactTitle;
}

interface BankBase extends Address {
  name: string;
  phone: string;
  fax: string;
  email: string;
  mcc_code_type: string;
  risk_contact: string;
  primary_contact: Contact;
  exclude_types: boolean;
  business_types: BusinessType[];
  product_types: ProductType[];
  checkout_types: CheckoutType[];
  about: string;
  status: BankStatus;
  is_deleted: Boolean;
}

export interface Bank extends BankBase {
  bid: number;
}

export interface Processor extends BankBase {
  acquiring_banks: string[] | number[];
  documents: ProcessorDocument[];
}

export enum GatewayStatus {
  Archived = -1,
  Inactive = 0,
  Active = 1,
  Pending = 2,
}

export const GatewayStatusLabels = {};
GatewayStatusLabels[GatewayStatus.Archived] = 'Archived';
GatewayStatusLabels[GatewayStatus.Inactive] = 'Inactive';
GatewayStatusLabels[GatewayStatus.Active] = 'Active';
GatewayStatusLabels[GatewayStatus.Pending] = 'Under Review';

export enum GatewayAdditionalOptions {
  IncludeRefunds = 1,
  BlockPrepaidCards = 2,
  BlockDebitCards = 3,
}

export const GatewayAdditionalOptionsList = [
  GatewayAdditionalOptions.IncludeRefunds,
  GatewayAdditionalOptions.BlockPrepaidCards,
  GatewayAdditionalOptions.BlockDebitCards,
];

export const GatewayAdditionalOptionLabels = {};
GatewayAdditionalOptionLabels[GatewayAdditionalOptions.IncludeRefunds] = 'Include Refunds';
GatewayAdditionalOptionLabels[GatewayAdditionalOptions.BlockPrepaidCards] = 'Block Prepaid Cards';
GatewayAdditionalOptionLabels[GatewayAdditionalOptions.BlockDebitCards] = 'Block Debit Cards';

export interface ProcessorConfigValues {
  [fieldset: string]: { [field: string]: string | number | boolean | null };
}

export enum ThreeDsVerificationStatus {
  Pending = 0,
  Verified = 1,
  Failed = 2,
}

export interface Gateway extends BaseModel {
  merchant_profile: string | number;
  name: string;
  is_internal: boolean;
  type: GatewayType;
  currency: string;
  merchant_id: string;
  terminal_number: string;
  acquirer_bin: string;
  timezone: string;
  bank: number;
  processor_type: ProcessorType;
  processor_config: ProcessorConfigValues;
  descriptor: string;
  level3_data_enabled: boolean;
  three_ds_enabled: boolean;
  visa_3ds_enabled: boolean;
  visa_3ds_acquirer_id: string;
  visa_3ds_merchant_id: string;
  visa_3ds_verification_status: ThreeDsVerificationStatus;
  mastercard_3ds_enabled: boolean;
  mastercard_3ds_acquirer_id: string;
  mastercard_3ds_merchant_id: string;
  mastercard_3ds_verification_status: ThreeDsVerificationStatus;
  amex_3ds_enabled: boolean;
  amex_3ds_acquirer_id: string;
  amex_3ds_merchant_id: string;
  amex_3ds_verification_status: ThreeDsVerificationStatus;
  discover_3ds_enabled: boolean;
  discover_3ds_acquirer_id: string;
  discover_3ds_merchant_id: string;
  discover_3ds_verification_status: ThreeDsVerificationStatus;
  additional_options: GatewayAdditionalOptions[];
  crm_merchants: number[];
  is_enabled: boolean;
  is_verified: boolean;
  is_deleted: boolean;
  organization_id: string;
  api_key: string;
  public_key: string;
  last_verified: string;
  health_score: string;
  approval_rate: string;
  monthly_cap: string;
  processor_name?: string;
  bank_name?: string;
  category?: number;
  showThreeDotMenu?: boolean;
}

export interface GatewayCategory extends BaseModel {
  name: string;
  slug?: string;
  created?: string;
}

export enum GatewayListingPopup {
  AssignGateway = 'assignGatewayPopup',
}

// The API has no status field; the listing and header derive one from these flags
export function getGatewayStatus(gateway: Gateway): GatewayStatus {
  let status = GatewayStatus.Inactive;

  if (gateway.is_deleted) {
    status = GatewayStatus.Archived;
  } else if (gateway.is_enabled && gateway.is_verified) {
    status = GatewayStatus.Active;
  } else if (gateway.is_enabled) {
    status = GatewayStatus.Pending;
  }

  return status;
}

// A 'select' field lists its choices in options
export interface ProcessorField extends CRMField {
  options?: SelectOption[];
}
export interface ProcessorConfig extends BaseModel {
  name: string;
  fields: ProcessorField[];
}

export enum ProcessorIdField {
  MerchantId = 'merchantId',
  TerminalNumber = 'terminalNumber',
}

export interface ProcessorIdRuleSet {
  [ProcessorIdField.MerchantId]?: FormatRule;
  [ProcessorIdField.TerminalNumber]?: FormatRule;
}

// Merchant/terminal number shapes per processor, mirroring checkouts/providers/processors/ on the backend.
// A rule makes that id required; Chase Payment Tech and VPC Qorpay have none, so they need neither.
export const ProcessorIdRules: { [processorType: number]: ProcessorIdRuleSet } = {};
ProcessorIdRules[ProcessorType.FDIGlobal] = {
  merchantId: { pattern: /^[0-9a-zA-Z]{1,11}$/, hint: '1-11 alphanumeric characters' },
  terminalNumber: { pattern: /^[0-9]{8}$/, hint: 'exactly 8 numeric digits' },
};
ProcessorIdRules[ProcessorType.Elavon] = {
  terminalNumber: { length: 22, hint: 'exactly 22 characters' },
};
ProcessorIdRules[ProcessorType.Tsys] = {
  merchantId: { length: 12, hint: 'exactly 12 characters' },
  terminalNumber: { length: 4, hint: 'exactly 4 characters' },
};
ProcessorIdRules[ProcessorType.SmartFDC] = {
  merchantId: { pattern: /^0000[0-9]{7}$/, hint: '0000 followed by 7 digits' },
  terminalNumber: { pattern: /^0000[0-9]{7}$/, hint: '0000 followed by 7 digits' },
};
ProcessorIdRules[ProcessorType.Streamline2] = {
  terminalNumber: { pattern: /^.{1,8}$/, hint: '1-8 characters' },
};

// 3DS acquirer and merchant ids, for every card network
export const ThreeDsIdRule: FormatRule = { pattern: /^[0-9a-zA-Z]+$/, hint: 'letters and numbers only, with no spaces' };

export enum ProcessorAddressField {
  Country = 'country',
  State = 'state',
}

// Processor configuration rules per fieldset and field name, mirroring validate_processor_config() in
// checkouts/providers/processors/ on the backend. Processors without an entry have no field rules.
export interface ProcessorConfigRuleSet {
  [fieldset: string]: { [field: string]: FormatRule };
}

export const ProcessorConfigRules: { [processorType: number]: ProcessorConfigRuleSet } = {};
ProcessorConfigRules[ProcessorType.Tsys] = {
  processor_fields: {
    bank_number: { pattern: /^[0-9]{6}$/, hint: 'exactly 6 digits' },
    chain_number: { pattern: /^[0-9]{6}$/, hint: 'exactly 6 digits' },
    merchant_bin_number: { pattern: /^[0-9]{6}$/, hint: 'exactly 6 digits' },
    store_id: { pattern: /^[0-9]{4}$/, hint: 'exactly 4 digits' },
    vital_number: { pattern: /^7[0-9]{7}$/, hint: '8 digits starting with 7' },
  },
  merchant_descriptor_information: {
    name: { pattern: /^[0-9a-zA-Z ]{1,38}$/, hint: '1-38 letters, numbers or spaces' },
    city: { pattern: /^[0-9a-zA-Z ]{1,21}$/, hint: '1-21 letters, numbers or spaces' },
    zip: { pattern: /^[0-9a-zA-Z ]{1,15}$/, hint: '1-15 letters, numbers or spaces' },
  },
};

export interface ThreeDsNetworkRow {
  key: string;
  label: string;
  icon: string;
  enabledControl: string;
  acquirerControl: string;
  merchantControl: string;
  statusControl: string;
  editing: boolean;
}


export enum ProcessorDocumentType {
  AgreementStatement = 1,
  FulfillmentAgreement = 2,
}

export const ProcessorDocumentTypeLabels = {};
ProcessorDocumentTypeLabels[ProcessorDocumentType.AgreementStatement] = 'Agreement Statement';
ProcessorDocumentTypeLabels[ProcessorDocumentType.FulfillmentAgreement] = 'Fulfillment Agreement';

export const ProcessorDocumentTypeList = [
  ProcessorDocumentType.AgreementStatement,
  ProcessorDocumentType.FulfillmentAgreement,
];

export enum ProcessorDocumentStatus {
  Pending = 0,
  Approved = 1,
  Suspended = 2,
}

export const ProcessorDocumentStatusLabels = {};
ProcessorDocumentStatusLabels[ProcessorDocumentStatus.Pending] = 'Under Review';
ProcessorDocumentStatusLabels[ProcessorDocumentStatus.Approved] = 'Approved';
ProcessorDocumentStatusLabels[ProcessorDocumentStatus.Suspended] = 'Suspended';

export const ProcessorDocumentStatusList = [
  ProcessorDocumentStatus.Pending,
  ProcessorDocumentStatus.Approved,
  ProcessorDocumentStatus.Suspended,
];

export interface ProcessorDocument extends BaseModel {
  name: string;
  file: { url: string; size: number };
  document_type: ProcessorDocumentType;
  description: string;
  status: ProcessorDocumentStatus;
  created: string;
  pre_signed_url: {};
}

export const FieldsToDisable = ['api_key', 'organization_id', 'monthly_cap'];

export enum BankInfoFields {
  Name = 'name',
  Address = 'address1',
  City = 'city',
  State = 'state',
  Zip = 'zip',
  Phone = 'phone',
  Email = 'email',
  BID = 'bid',
}

export enum ProcessorInfoFields {
  Name = 'name',
  Address = 'address1',
  City = 'city',
  State = 'state',
  Zip = 'zip',
  Phone = 'phone',
  Email = 'email',
  AcquiringBank = 'acquiring_banks',
}

export enum ContactInfoFields {
  FirstName = 'primary_contact.first_name',
  LastName = 'primary_contact.last_name',
  Email = 'primary_contact.email',
  Phone = 'primary_contact.phone',
  RiskContact = 'risk_contact',
}


export enum AllowedDocumentTypes {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ZIP = 'application/x-zip-compressed',
}

export const CheckoutMerchantPopupText = {
  VerifiedText:
    'Access a curated gallery of high converting checkouts, verified and approved by card brands. ' +
    'Enjoy higher approval rates and a better bottom line.',
  UnverifiedText:
    'Non verified simply means the checkouts are not considered verified and controlled. ' +
    'You have more flexibility to edit as you wish. Get full access to a curated gallery of checkout and',
};

export const TemplateTypeMap: { [key: number]: string } = {
  [CheckoutTemplateType.ShoppingCart]: 'Cart',
  [CheckoutTemplateType.FreeTrial]: 'Free-Trial',
  [CheckoutTemplateType.OneTimePurchase]: 'One-Time',
  [CheckoutTemplateType.BundlePackage]: 'Bundle',
  [CheckoutTemplateType.FixedSubscription]: 'Subscription',
};

export const CheckoutPopupTitleMap: { [key: number]: string } = {
  [CheckoutPopup.Create]: 'Create New Checkout',
  [CheckoutPopup.Update]: 'Rename Checkout',
  [CheckoutPopup.Duplicate]: 'Duplicate Checkout',
  [CheckoutPopup.Archive]: 'Archive Checkout',
  [CheckoutPopup.Unarchive]: 'Unarchive Checkout',
};

export const UpsellPopupTitleMap: { [key: number]: string } = {
  [CheckoutPopup.Create]: 'Create New Upsell',
  [CheckoutPopup.Update]: 'Rename Upsell',
  [CheckoutPopup.Duplicate]: 'Duplicate Upsell',
  [CheckoutPopup.Delete]: 'Delete Upsell',
};

export const PopupActionMap: { [key: number]: string } = {
  [CheckoutPopup.Update]: 'Updated',
  [CheckoutPopup.Duplicate]: 'Duplicated',
  [CheckoutPopup.Delete]: 'Deleted',
  [CheckoutPopup.Archive]: 'Archived',
  [CheckoutPopup.Unarchive]: 'Unarchived',
};

// Timezones supported by CyberSource
export const Timezones = [
  'Pacific/Pago_Pago',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Vancouver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Edmonton',
  'America/Denver',
  'America/Winnipeg',
  'America/Mexico_City',
  'America/Chicago',
  'America/Bogota',
  'America/Indianapolis',
  'America/New_York',
  'America/La_Paz',
  'America/Halifax',
  'America/St_Johns',
  'America/Buenos_Aires',
  'America/Godthab',
  'America/Sao_Paulo',
  'America/Noronha',
  'Atlantic/Cape_Verde',
  'GMT',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/London',
  'Africa/Tunis',
  'Europe/Vienna',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Prague',
  'Europe/Berlin',
  'Europe/Copenhagen',
  'Europe/Madrid',
  'Europe/Budapest',
  'Europe/Rome',
  'Africa/Tripoli',
  'Europe/Monaco',
  'Europe/Malta',
  'Europe/Amsterdam',
  'Europe/Oslo',
  'Europe/Warsaw',
  'Europe/Stockholm',
  'Europe/Belgrade',
  'Europe/Paris',
  'Africa/Johannesburg',
  'Europe/Minsk',
  'Africa/Cairo',
  'Europe/Helsinki',
  'Europe/Athens',
  'Asia/Jerusalem',
  'Europe/Riga',
  'Europe/Bucharest',
  'Europe/Istanbul',
  'Asia/Riyadh',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Baku',
  'Asia/Tbilisi',
  'Asia/Calcutta',
  'Asia/Katmandu',
  'Asia/Dacca',
  'Asia/Rangoon',
  'Asia/Jakarta',
  'Asia/Saigon',
  'Asia/Bangkok',
  'Australia/Perth',
  'Asia/Hong_Kong',
  'Asia/Macao',
  'Asia/Kuala_Lumpur',
  'Asia/Manila',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Asia/Yakutsk',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Broken_Hill',
  'Australia/Darwin',
  'Australia/Eucla',
  'Australia/Hobart',
  'Australia/Lindeman',
  'Australia/Sydney',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Asia/Magadan',
  'Pacific/Norfolk',
  'Pacific/Auckland',
];
